import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Warehouse,
  Loader2,
  Plus,
  Trash2,
  Printer,
  ChevronDown,
  ChevronRight,
  Thermometer,
  Package,
  QrCode,
  Play,
  Search
} from 'lucide-react';
import { etiquetaPosicao, imprimirEtiquetasQr } from './armazemUtils';
import { insumoCorrespondeTermo } from '../../utils/insumoBusca';

const call = (name, data) => httpsCallable(functions, name)(data);

const nomeProduto = (p) =>
  String(p?.nome || p?.produto_nome || p?.codigo || p?.codigo_produto || p?.id || '').trim();

const ArmazenamentoPage = () => {
  const { getEmpresaId, isRole } = useAuth();
  const navigate = useNavigate();
  const empresaId = getEmpresaId();
  const podeCadastrar = isRole('superadmin') || isRole('admin') || isRole('administrador') || isRole('gerente');

  const [ativo, setAtivo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [estrutura, setEstrutura] = useState({ camaras: [], ruas: [], estantes: [], andares: [], posicoes: [] });
  const [produtos, setProdutos] = useState([]);
  const [expand, setExpand] = useState({});
  const [formCam, setFormCam] = useState(null);
  const [buscaMp, setBuscaMp] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setErr('');
    setLoading(true);
    try {
      const st = await call('getStatusModuloArmazenamento', { empresa_id: empresaId });
      const on = !!st.data?.data?.ativo;
      setAtivo(on);
      if (!on) {
        setEstrutura({ camaras: [], ruas: [], estantes: [], andares: [], posicoes: [] });
        return;
      }
      const [est, pr] = await Promise.all([
        call('listEstruturaArmazem', { empresa_id: empresaId }),
        call('listProducts', { empresa_id: empresaId, limit: 400 })
      ]);
      setEstrutura(est.data?.data || { camaras: [], ruas: [], estantes: [], andares: [], posicoes: [] });
      const items = pr.data?.data?.products || pr.data?.data?.items || [];
      setProdutos(Array.isArray(items) ? items : []);
    } catch (e) {
      setErr(e.message || e.details || 'Erro ao carregar armazenamento');
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!formCam) setBuscaMp('');
  }, [formCam]);

  const produtosFiltrados = useMemo(() => {
    const lista = (produtos || []).filter((p) => insumoCorrespondeTermo(p, buscaMp));
    return [...lista].sort((a, b) =>
      nomeProduto(a).localeCompare(nomeProduto(b), 'pt-BR', { sensitivity: 'base' })
    );
  }, [produtos, buscaMp]);

  const toggleModulo = async (on) => {
    setBusy(true);
    setErr('');
    try {
      await call('setModuloArmazenamento', { empresa_id: empresaId, ativo: on });
      setMsg(on ? 'Módulo de Armazenamento ativado.' : 'Módulo desativado.');
      await load();
    } catch (e) {
      setErr(e.message || 'Não foi possível alterar o módulo');
    } finally {
      setBusy(false);
    }
  };

  const ruasDa = (camId) => estrutura.ruas.filter((r) => r.camara_id === camId);
  const estDa = (ruaId) => estrutura.estantes.filter((e) => e.rua_id === ruaId);
  const andDa = (estId) => estrutura.andares.filter((a) => a.estante_id === estId);
  const posDa = (andId) => estrutura.posicoes.filter((p) => p.andar_id === andId);

  const salvarCamara = async (ev) => {
    ev.preventDefault();
    setBusy(true);
    setErr('');
    try {
      await call('upsertArmazemCamara', { empresa_id: empresaId, ...formCam });
      setFormCam(null);
      setMsg('Câmara salva.');
      await load();
    } catch (e) {
      setErr(e.message || 'Erro ao salvar câmara');
    } finally {
      setBusy(false);
    }
  };

  const criarFilho = async (fn, payload, okMsg) => {
    const valor = window.prompt(okMsg);
    if (valor == null) return;
    setBusy(true);
    setErr('');
    try {
      await call(fn, { empresa_id: empresaId, ...payload, codigo: valor.trim().toUpperCase(), nome: valor.trim() });
      await load();
    } catch (e) {
      setErr(e.message || 'Erro ao criar');
    } finally {
      setBusy(false);
    }
  };

  const criarPosicoes = async (andarId) => {
    const n = Number(window.prompt('Quantos porta-pallets criar neste andar?', '4'));
    if (!Number.isFinite(n) || n <= 0) return;
    setBusy(true);
    try {
      await call('upsertArmazemPosicao', { empresa_id: empresaId, andar_id: andarId, quantidade: n });
      await load();
    } catch (e) {
      setErr(e.message || 'Erro ao criar porta-pallets');
    } finally {
      setBusy(false);
    }
  };

  const excluir = async (fn, id, label) => {
    if (!window.confirm(`Excluir ${label}?`)) return;
    setBusy(true);
    try {
      await call(fn, { empresa_id: empresaId, id });
      await load();
    } catch (e) {
      setErr(e.message || 'Não foi possível excluir');
    } finally {
      setBusy(false);
    }
  };

  const imprimirPosicoes = (lista) => {
    imprimirEtiquetasQr(lista.map(etiquetaPosicao), 'Etiquetas de porta-pallet');
  };

  const ocupadas = useMemo(() => estrutura.posicoes.filter((p) => p.ocupada).length, [estrutura.posicoes]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500 gap-2">
        <Loader2 className="h-6 w-6 animate-spin" /> Carregando armazenamento…
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Warehouse className="h-7 w-7 text-emerald-700" />
            Armazenamento
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Câmaras, ruas, estantes, andares e porta-pallets. Imprima o QR de cada posição e use o tablet
            para guardar e retirar.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {ativo && (
            <button
              type="button"
              onClick={() => navigate('/app/armazem-operacao')}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold"
            >
              <Play className="h-4 w-4" /> Operação no tablet
            </button>
          )}
          {podeCadastrar && (
            <button
              type="button"
              disabled={busy}
              onClick={() => toggleModulo(!ativo)}
              className={`px-3 py-2 rounded-lg text-sm font-medium border ${
                ativo ? 'border-emerald-300 bg-emerald-50 text-emerald-900' : 'border-gray-300 bg-white'
              }`}
            >
              Módulo {ativo ? 'ativo' : 'inativo'}
            </button>
          )}
        </div>
      </div>

      {err && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800">{err}</div>}
      {msg && <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-900">{msg}</div>}

      {!ativo ? (
        <div className="rounded-xl border bg-white p-8 text-center text-gray-600">
          O módulo está desligado. Ative-o para cadastrar a estrutura e orientar a guarda dos pallets após o
          recebimento.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div className="rounded-lg border bg-white p-3">
              <p className="text-xs text-gray-500">Câmaras</p>
              <p className="text-xl font-semibold">{estrutura.camaras.length}</p>
            </div>
            <div className="rounded-lg border bg-white p-3">
              <p className="text-xs text-gray-500">Porta-pallets</p>
              <p className="text-xl font-semibold">{estrutura.posicoes.length}</p>
            </div>
            <div className="rounded-lg border bg-white p-3">
              <p className="text-xs text-gray-500">Ocupados</p>
              <p className="text-xl font-semibold">{ocupadas}</p>
            </div>
          </div>

          {podeCadastrar && (
            <div className="flex justify-end">
              <button
                type="button"
                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-emerald-600 text-emerald-800 text-sm"
                onClick={() =>
                  setFormCam({
                    codigo: `C${String(estrutura.camaras.length + 1).padStart(2, '0')}`,
                    nome: '',
                    temp_min: 0,
                    temp_max: 8,
                    produtos: []
                  })
                }
              >
                <Plus className="h-4 w-4" /> Nova câmara
              </button>
            </div>
          )}

          {formCam && (
            <form onSubmit={salvarCamara} className="rounded-xl border bg-white p-4 space-y-3">
              <h3 className="font-semibold text-gray-900">{formCam.id ? 'Editar câmara' : 'Nova câmara'}</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="text-sm">
                  Código
                  <input
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={formCam.codigo}
                    onChange={(e) => setFormCam({ ...formCam, codigo: e.target.value })}
                    required
                  />
                </label>
                <label className="text-sm">
                  Nome
                  <input
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={formCam.nome}
                    onChange={(e) => setFormCam({ ...formCam, nome: e.target.value })}
                    required
                  />
                </label>
                <label className="text-sm">
                  Temp. mín. (°C)
                  <input
                    type="number"
                    step="0.1"
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={formCam.temp_min}
                    onChange={(e) => setFormCam({ ...formCam, temp_min: e.target.value })}
                    required
                  />
                </label>
                <label className="text-sm">
                  Temp. máx. (°C)
                  <input
                    type="number"
                    step="0.1"
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={formCam.temp_max}
                    onChange={(e) => setFormCam({ ...formCam, temp_max: e.target.value })}
                    required
                  />
                </label>
              </div>
              <div>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <p className="text-sm font-medium text-gray-700">Matérias-primas permitidas</p>
                  <p className="text-xs text-gray-500">
                    {(formCam.produtos || []).length} selecionada
                    {(formCam.produtos || []).length === 1 ? '' : 's'}
                  </p>
                </div>
                <div className="relative mb-2">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="search"
                    className="w-full border rounded-lg pl-8 pr-3 py-2 text-sm"
                    placeholder="Buscar por nome ou código…"
                    value={buscaMp}
                    onChange={(e) => setBuscaMp(e.target.value)}
                    autoComplete="off"
                  />
                </div>
                <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1">
                  {produtosFiltrados.map((p) => {
                    const id = p.id;
                    const checked = (formCam.produtos || []).some((x) => x.id === id);
                    const codigo = p.codigo || p.codigo_produto || p.codigo_origem;
                    return (
                      <label key={id} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const cur = formCam.produtos || [];
                            setFormCam({
                              ...formCam,
                              produtos: e.target.checked
                                ? [...cur, { id, nome: p.nome || p.produto_nome }]
                                : cur.filter((x) => x.id !== id)
                            });
                          }}
                        />
                        <span>
                          {p.nome || p.produto_nome || id}
                          {codigo ? <span className="text-xs text-gray-500"> · {codigo}</span> : null}
                        </span>
                      </label>
                    );
                  })}
                  {produtos.length === 0 && (
                    <p className="text-xs text-gray-500">Nenhum insumo cadastrado.</p>
                  )}
                  {produtos.length > 0 && produtosFiltrados.length === 0 && (
                    <p className="text-xs text-gray-500">Nenhuma matéria-prima encontrada para “{buscaMp}”.</p>
                  )}
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <button type="button" className="px-3 py-2 border rounded-lg text-sm" onClick={() => setFormCam(null)}>
                  Cancelar
                </button>
                <button type="submit" disabled={busy} className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm">
                  Salvar câmara
                </button>
              </div>
            </form>
          )}

          <div className="space-y-2">
            {estrutura.camaras.length === 0 && (
              <p className="text-sm text-gray-500 bg-white border rounded-lg p-6 text-center">
                Nenhuma câmara cadastrada.
              </p>
            )}
            {estrutura.camaras.map((c) => {
              const open = !!expand[c.id];
              const posCam = estrutura.posicoes.filter((p) => p.camara_id === c.id);
              return (
                <div key={c.id} className="bg-white border rounded-xl overflow-hidden">
                  <button
                    type="button"
                    className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-gray-50"
                    onClick={() => setExpand((s) => ({ ...s, [c.id]: !open }))}
                  >
                    {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    <span className="font-semibold text-gray-900">{c.codigo}</span>
                    <span className="text-gray-600">{c.nome}</span>
                    <span className="ml-auto text-xs text-gray-500 inline-flex items-center gap-1">
                      <Thermometer className="h-3.5 w-3.5" />
                      {c.temp_min}° a {c.temp_max}°C · {posCam.filter((p) => p.ocupada).length}/{posCam.length} ocup.
                    </span>
                  </button>
                  {open && (
                    <div className="border-t px-4 py-3 space-y-3 bg-gray-50">
                      <p className="text-xs text-gray-600 flex items-center gap-1">
                        <Package className="h-3.5 w-3.5" />
                        {(c.produtos_nomes || []).join(', ') || 'Sem produtos'}
                      </p>
                      {podeCadastrar && (
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            className="text-xs px-2 py-1 border rounded"
                            onClick={() =>
                              setFormCam({
                                id: c.id,
                                codigo: c.codigo,
                                nome: c.nome,
                                temp_min: c.temp_min,
                                temp_max: c.temp_max,
                                produtos: (c.produtos_ids || []).map((id, i) => ({
                                  id,
                                  nome: (c.produtos_nomes || [])[i] || id
                                }))
                              })
                            }
                          >
                            Editar câmara
                          </button>
                          <button
                            type="button"
                            className="text-xs px-2 py-1 border rounded"
                            onClick={() => criarFilho('upsertArmazemRua', { camara_id: c.id }, 'Código da rua (ex.: R01)')}
                          >
                            + Rua
                          </button>
                          {posCam.length > 0 && (
                            <button
                              type="button"
                              className="text-xs px-2 py-1 border rounded inline-flex items-center gap-1"
                              onClick={() => imprimirPosicoes(posCam)}
                            >
                              <Printer className="h-3 w-3" /> QR da câmara
                            </button>
                          )}
                          <button
                            type="button"
                            className="text-xs px-2 py-1 text-red-700"
                            onClick={() => excluir('deleteArmazemCamara', c.id, `a câmara ${c.codigo}`)}
                          >
                            Excluir
                          </button>
                        </div>
                      )}
                      {ruasDa(c.id).map((r) => (
                        <div key={r.id} className="ml-2 border-l-2 border-emerald-200 pl-3 space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="font-medium">Rua {r.codigo}</span>
                            {podeCadastrar && (
                              <>
                                <button
                                  type="button"
                                  className="text-xs text-emerald-800"
                                  onClick={() =>
                                    criarFilho('upsertArmazemEstante', { rua_id: r.id }, 'Código da estante (ex.: E01)')
                                  }
                                >
                                  + Estante
                                </button>
                                <button
                                  type="button"
                                  className="text-xs text-red-600"
                                  onClick={() => excluir('deleteArmazemRua', r.id, `a rua ${r.codigo}`)}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </>
                            )}
                          </div>
                          {estDa(r.id).map((e) => (
                            <div key={e.id} className="ml-2 space-y-1">
                              <div className="flex items-center gap-2 text-sm">
                                <span>Estante {e.codigo}</span>
                                {podeCadastrar && (
                                  <>
                                    <button
                                      type="button"
                                      className="text-xs text-emerald-800"
                                      onClick={() =>
                                        criarFilho(
                                          'upsertArmazemAndar',
                                          { estante_id: e.id, numero: andDa(e.id).length + 1 },
                                          'Código do andar (ex.: A01)'
                                        )
                                      }
                                    >
                                      + Andar
                                    </button>
                                    <button
                                      type="button"
                                      className="text-xs text-red-600"
                                      onClick={() => excluir('deleteArmazemEstante', e.id, `a estante ${e.codigo}`)}
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </button>
                                  </>
                                )}
                              </div>
                              {andDa(e.id).map((a) => (
                                <div key={a.id} className="ml-3 rounded-lg bg-white border p-2">
                                  <div className="flex items-center justify-between text-sm">
                                    <span>
                                      Andar {a.codigo} ({a.nome})
                                    </span>
                                    {podeCadastrar && (
                                      <div className="flex gap-2">
                                        <button
                                          type="button"
                                          className="text-xs text-emerald-800"
                                          onClick={() => criarPosicoes(a.id)}
                                        >
                                          + Porta-pallet
                                        </button>
                                        <button
                                          type="button"
                                          className="text-xs text-red-600"
                                          onClick={() => excluir('deleteArmazemAndar', a.id, `o andar ${a.codigo}`)}
                                        >
                                          <Trash2 className="h-3 w-3" />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                  <div className="mt-2 flex flex-wrap gap-1">
                                    {posDa(a.id).map((p) => (
                                      <span
                                        key={p.id}
                                        className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border ${
                                          p.ocupada
                                            ? 'bg-amber-50 border-amber-300 text-amber-900'
                                            : 'bg-emerald-50 border-emerald-200 text-emerald-900'
                                        }`}
                                      >
                                        <QrCode className="h-3 w-3" />
                                        {p.endereco}
                                        {podeCadastrar && !p.ocupada && (
                                          <button
                                            type="button"
                                            className="text-red-600"
                                            onClick={() => excluir('deleteArmazemPosicao', p.id, p.endereco)}
                                          >
                                            ×
                                          </button>
                                        )}
                                      </span>
                                    ))}
                                    {posDa(a.id).length > 0 && (
                                      <button
                                        type="button"
                                        className="text-[11px] underline"
                                        onClick={() => imprimirPosicoes(posDa(a.id))}
                                      >
                                        Imprimir QR
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default ArmazenamentoPage;
